import React, { useEffect, useCallback, useRef, useState } from 'react';
import styled from 'astroturf/react';
import { Body } from 'js/design_system/Body';
import { Spinner } from 'js/design_system/Spinner';
import { Icon } from 'js/design_system/Icon';
import { useBreakpoint } from 'js/design_system/hooks/useBreakpoint';
import { listReports, ReportListItem, ReportStatus } from 'js/citeright/api';

type StatusConfig = {
  label: string;
  color: string;
  backgroundColor: string;
  icon?: string;
};

const STATUS_CONFIGS: Record<ReportStatus, StatusConfig> = {
  completed: {
    label: 'Completed',
    color: 'var(--success-dark)',
    backgroundColor: 'var(--success-light)',
    icon: 'check_circle',
  },
  processing: {
    label: 'Processing',
    color: 'var(--brand-colors-info-900)',
    backgroundColor: 'var(--brand-colors-info-100)',
    icon: 'pending',
  },
  expired: {
    label: 'Expired',
    color: 'var(--text-secondary)',
    backgroundColor: 'var(--brand-colors-neutral-300)',
    icon: 'schedule',
  },
  error: {
    label: 'Error',
    color: 'var(--text-error)',
    backgroundColor: 'var(--error-light)',
    icon: 'error',
  },
  pending: {
    label: 'Pending',
    color: 'var(--text-secondary)',
    backgroundColor: 'var(--brand-colors-neutral-300)',
    icon: 'hourglass_empty',
  },
};

function formatDate(isoString: string): string {
  const date = new Date(isoString);
  const datePart = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const timePart = date
    .toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
    .toLowerCase()
    .replace(' ', '');
  return `${datePart} at ${timePart}`;
}

function handleKeyDown(e: React.KeyboardEvent, callback: () => void): void {
  if (e.key === 'Enter' || e.key === ' ') {
    callback();
  }
}

type ReportListProps = {
  onSelectReport: (report: ReportListItem) => void;
};

export const ReportList: React.FC<ReportListProps> = ({ onSelectReport }) => {
  const [reports, setReports] = useState<ReportListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const loadingMoreRef = useRef(loadingMore);
  const pageRef = useRef(page);
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === 'small';

  loadingMoreRef.current = loadingMore;
  pageRef.current = page;

  const loadReports = useCallback(async (pageNum: number, append = false) => {
    try {
      if (pageNum === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError(null);

      const response = await listReports(pageNum, 10);

      if (append) {
        setReports((prev) => [...prev, ...response.reports]);
      } else {
        setReports(response.reports);
      }
      setHasMore(response.pagination.has_more);
      setPage(pageNum);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load reports');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    loadReports(1);
  }, [loadReports]);

  useEffect(() => {
    if (loading || !hasMore) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMoreRef.current) {
          loadReports(pageRef.current + 1, true);
        }
      },
      { threshold: 0.1 },
    );

    if (sentinelRef.current) {
      observerRef.current.observe(sentinelRef.current);
    }

    return () => {
      observerRef.current?.disconnect();
    };
  }, [loading, hasMore, loadReports]);

  if (loading) {
    return (
      <LoadingContainer>
        <Spinner size={32} />
        <LoadingText>Loading reports...</LoadingText>
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <ErrorContainer>
        <Icon name="error" size={32} />
        <ErrorText>{error}</ErrorText>
      </ErrorContainer>
    );
  }

  if (reports.length === 0) {
    return (
      <EmptyContainer>
        <Icon name="description" size={48} />
        <EmptyTitle>No reports yet</EmptyTitle>
        <EmptyText>
          Upload a document to create your first citation report.
        </EmptyText>
      </EmptyContainer>
    );
  }

  return (
    <Container>
      {isMobile ? (
        <MobileList>
          {reports.map((report) => (
            <MobileCard
              key={report.id}
              onClick={() => onSelectReport(report)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => handleKeyDown(e, () => onSelectReport(report))}
            >
              <MobileCardHeader>
                <MobileTitle>{report.title}</MobileTitle>
                <StatusBadge status={report.status} />
              </MobileCardHeader>
              <MobileCardMeta>
                <MetaItem>
                  <Icon name="insert_drive_file" size={16} />
                  <span>{report.file_type}</span>
                </MetaItem>
                <MetaItem>
                  <Icon name="schedule" size={16} />
                  <span>{formatDate(report.updated_at)}</span>
                </MetaItem>
              </MobileCardMeta>
            </MobileCard>
          ))}
        </MobileList>
      ) : (
        <DesktopTable>
          <TableHeader>
            <HeaderCell style={{ flex: 3 }}>Document Title</HeaderCell>
            <HeaderCell style={{ flex: 1 }}>Type</HeaderCell>
            <HeaderCell style={{ flex: 1 }}>Status</HeaderCell>
            <HeaderCell style={{ flex: 1 }}>Last Updated</HeaderCell>
          </TableHeader>
          <TableBody>
            {reports.map((report) => (
              <TableRow
                key={report.id}
                onClick={() => onSelectReport(report)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) =>
                  handleKeyDown(e, () => onSelectReport(report))
                }
              >
                <TableCell style={{ flex: 3 }}>
                  <TitleText>{report.title}</TitleText>
                </TableCell>
                <TableCell style={{ flex: 1 }}>
                  <TypeText>{report.file_type}</TypeText>
                </TableCell>
                <TableCell style={{ flex: 1 }}>
                  <StatusBadge status={report.status} />
                </TableCell>
                <TableCell style={{ flex: 1 }}>
                  <DateText>{formatDate(report.updated_at)}</DateText>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </DesktopTable>
      )}

      <Sentinel ref={sentinelRef}>
        {loadingMore && (
          <LoadingMoreContainer>
            <Spinner size={24} />
          </LoadingMoreContainer>
        )}
      </Sentinel>
    </Container>
  );
};

const StatusBadge: React.FC<{ status: ReportStatus }> = ({ status }) => {
  const config = STATUS_CONFIGS[status];
  return (
    <BadgeContainer
      style={{ color: config.color, backgroundColor: config.backgroundColor }}
    >
      {config.icon && <Icon name={config.icon} size={14} />}
      <span>{config.label}</span>
    </BadgeContainer>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: 60vh;
  overflow-y: auto;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-lg-64, 64px) var(--spacing-sm-24, 24px);
  gap: var(--spacing-xs-16, 16px);
`;

const LoadingText = styled(Body).attrs({ size: 'md' })`
  color: var(--text-secondary, #535366);
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-lg-64, 64px) var(--spacing-sm-24, 24px);
  gap: var(--spacing-xs-16, 16px);
  color: var(--text-error, #b60000);
`;

const ErrorText = styled(Body).attrs({ size: 'md' })`
  color: var(--text-error, #b60000);
  text-align: center;
`;

const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-lg-64, 64px) var(--spacing-sm-24, 24px);
  gap: var(--spacing-xs-16, 16px);
  color: var(--text-inactive, #777164);
`;

const EmptyTitle = styled(Body).attrs({ size: 'lg' })`
  color: var(--text-primary, #141413);
  font-weight: 600;
`;

const EmptyText = styled(Body).attrs({ size: 'md' })`
  color: var(--text-secondary, #535366);
  text-align: center;
`;

const MobileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-12, 12px);
  padding: var(--spacing-xs-16, 16px);
`;

const MobileCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-8, 8px);
  padding: var(--spacing-xs-16, 16px);
  background: var(--background-white, #fff);
  border: 1px solid var(--stroke-light, #dddde2);
  border-radius: var(--border-radius-radius-md, 8px);
  cursor: pointer;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  &:focus-visible {
    outline: 2px solid var(--primary-brand, #0645b1);
    outline-offset: 2px;
  }
`;

const MobileCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--spacing-xs-12, 12px);
`;

const MobileTitle = styled(Body).attrs({ size: 'md' })`
  color: var(--text-primary, #141413);
  font-weight: 500;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const MobileCardMeta = styled.div`
  display: flex;
  gap: var(--spacing-sm-24, 24px);
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs-4, 4px);
  color: var(--text-inactive, #777164);
  font-size: var(--type-body-sm-size, 14px);
`;

const DesktopTable = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const TableHeader = styled.div`
  display: flex;
  padding: var(--spacing-xs-12, 12px) var(--spacing-xs-16, 16px);
  background: var(--background-beige-light, #f9f8f6);
  border-bottom: 1px solid var(--stroke-light, #dddde2);
`;

const HeaderCell = styled.div`
  color: var(--text-secondary, #535366);
  font-size: var(--type-body-sm-size, 14px);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const TableBody = styled.div`
  display: flex;
  flex-direction: column;
`;

const TableRow = styled.div`
  display: flex;
  padding: var(--spacing-xs-16, 16px);
  border-bottom: 1px solid var(--stroke-light, #dddde2);
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: var(--background-beige-light, #f9f8f6);
  }

  &:focus-visible {
    outline: 2px solid var(--primary-brand, #0645b1);
    outline-offset: -2px;
  }
`;

const TableCell = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  overflow: hidden;
`;

const TitleText = styled(Body).attrs({ size: 'md' })`
  color: var(--text-primary, #141413);
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TypeText = styled(Body).attrs({ size: 'sm' })`
  color: var(--text-secondary, #535366);
`;

const DateText = styled(Body).attrs({ size: 'sm' })`
  color: var(--text-inactive, #777164);
`;

const BadgeContainer = styled.span`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs-4, 4px);
  padding: var(--spacing-xs-4, 4px) var(--spacing-xs-8, 8px);
  border-radius: var(--border-radius-radius-sm, 4px);
  font-size: var(--type-body-xs-size, 12px);
  font-weight: 500;
  white-space: nowrap;
`;

const Sentinel = styled.div`
  min-height: 1px;
`;

const LoadingMoreContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: var(--spacing-xs-16, 16px);
`;

export default ReportList;
